import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, AlertTriangle, CheckCircle2, XCircle, Shield, Clock, FileText, Users, GitBranch, Activity, ChevronDown, ChevronUp, X, TrendingUp, Zap, HelpCircle, User, Briefcase, Globe, Link2, Bot, MessageSquare, Send, Loader } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';

// Stage descriptions for help tooltips
const STAGE_INFO = {
  A_INTAKE: {
    name: 'Intake',
    description: 'Initial onboarding declaration and terms acceptance. Customer confirms they will provide accurate information and comply with verification requirements.',
    purpose: 'Establishes legal basis for data collection and sets expectations for the verification process.'
  },
  B_ENTITY_ID: {
    name: 'Entity Identification',
    description: 'Identifies the type of entity (UK company, overseas, partnership, etc.) and collects core business identifiers like company number, registered address, and tax IDs.',
    purpose: 'Verifies the legal existence of the entity through official registries (Companies House, etc.) and runs initial sanctions/adverse media screening.'
  },
  C_BUSINESS_PROFILE: {
    name: 'Business Profile',
    description: 'Captures the nature of the business including industry sector, purpose of relationship, and expected transaction volumes.',
    purpose: 'Identifies high-risk industries (crypto, gambling, arms) and unusual business patterns that may require enhanced due diligence.'
  },
  D_GEOGRAPHY: {
    name: 'Geographic Risk',
    description: 'Assesses where the business operates, has customers, or sources funds from.',
    purpose: 'Identifies exposure to high-risk jurisdictions (FATF grey/blacklist, sanctioned countries) that trigger enhanced due diligence requirements.'
  },
  E_OWNERSHIP: {
    name: 'Ownership & Control',
    description: 'Maps the ownership structure to identify Ultimate Beneficial Owners (UBOs) with >25% ownership and persons with significant control.',
    purpose: 'Ensures transparency of ownership, identifies complex structures that may obscure beneficial ownership, and verifies identity of key individuals.'
  },
  F_NOMINEE_PROXY: {
    name: 'Nominee/Proxy Check',
    description: 'Determines if any parties are acting as nominees, trustees, or proxies for undisclosed beneficial owners.',
    purpose: 'Identifies potential attempts to hide true beneficial ownership which is a significant money laundering red flag.'
  },
  G_PRODUCT_RISK: {
    name: 'Product Risk',
    description: 'Assesses risks related to the specific products/services the customer will use, including automated decision-making tools.',
    purpose: 'Ensures appropriate governance for high-risk product usage and identifies need for model risk management.'
  },
  H_FUNDS_WEALTH: {
    name: 'Source of Funds/Wealth',
    description: 'Collects evidence of where the customer\'s funds originate and, for high-risk cases, the source of wealth for UBOs.',
    purpose: 'Critical for anti-money laundering - ensures funds are from legitimate sources and not proceeds of crime.'
  },
  I_MEDIA_SCREENING: {
    name: 'Media Screening',
    description: 'Runs enhanced adverse media checks for entities and key individuals when triggered by risk factors.',
    purpose: 'Identifies negative news, legal issues, or reputational concerns that may indicate elevated risk.'
  },
  J_DECISION: {
    name: 'Decision & Approval',
    description: 'Computes final risk tier, determines approval decision, and routes high-risk cases for senior management approval.',
    purpose: 'Ensures appropriate oversight for high-risk relationships and assigns ongoing monitoring requirements.'
  }
};

// Decision Tree Visual Component with help icons
const DecisionTreeVisual = ({ nodes, completedNodes, auditLog, answers, onNodeClick, onStageHelp }) => {
  const stages = useMemo(() => {
    const stageMap = {};
    const stageOrder = ['A_INTAKE', 'B_ENTITY_ID', 'C_BUSINESS_PROFILE', 'D_GEOGRAPHY', 'E_OWNERSHIP', 'F_NOMINEE_PROXY', 'G_PRODUCT_RISK', 'H_FUNDS_WEALTH', 'I_MEDIA_SCREENING', 'J_DECISION'];
    nodes.forEach(node => {
      if (!stageMap[node.stage]) stageMap[node.stage] = [];
      stageMap[node.stage].push(node);
    });
    return stageOrder.filter(s => stageMap[s]).map(s => ({ stage: s, nodes: stageMap[s] }));
  }, [nodes]);

  const getNodeColor = (node) => {
    if (!completedNodes.includes(node.id)) return 'bg-gray-100 border-gray-300 text-gray-400';
    const auditEntry = auditLog.find(a => a.node_id === node.id && a.action === 'ANSWER_PROCESSED');
    if (auditEntry?.score_delta > 0) return 'bg-red-100 border-red-400 text-red-800';
    if (auditEntry?.triggers_added?.length > 0) return 'bg-yellow-100 border-yellow-400 text-yellow-800';
    return 'bg-green-100 border-green-400 text-green-800';
  };

  return (
    <div className="space-y-4">
      {stages.map(({ stage, nodes: stageNodes }) => {
        const stageInfo = STAGE_INFO[stage];
        const hasCompletedNodes = stageNodes.some(n => completedNodes.includes(n.id));
        return (
          <div key={stage} className={`relative p-3 rounded-lg ${hasCompletedNodes ? 'bg-gray-50' : 'bg-gray-25'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{stageInfo?.name || stage}</span>
              <button onClick={() => onStageHelp(stage)} className="text-gray-400 hover:text-blue-600 transition-colors">
                <HelpCircle className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {stageNodes.map(node => {
                const isCompleted = completedNodes.includes(node.id);
                const auditEntry = auditLog.find(a => a.node_id === node.id && a.action === 'ANSWER_PROCESSED');
                return (
                  <button
                    key={node.id}
                    onClick={() => isCompleted && onNodeClick(node, auditEntry)}
                    disabled={!isCompleted}
                    title={node.question}
                    className={`relative px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${getNodeColor(node)} ${isCompleted ? 'cursor-pointer hover:shadow-md hover:scale-105' : 'cursor-not-allowed opacity-50'}`}
                  >
                    <span className="font-bold">{node.id}</span>
                    {auditEntry?.score_delta > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">+{auditEntry.score_delta}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Node Detail Modal
const NodeDetailModal = ({ node, auditEntry, answer, onClose }) => {
  if (!node) return null;

  const typeLabels = {
    single_select: 'Single Choice',
    multi_select: 'Multiple Choice',
    data_capture: 'Data Entry',
    checks: 'Automated Checks',
    router: 'Decision Router',
    compute: 'Risk Calculation',
    finalize: 'Finalization',
    evidence_request: 'Evidence Upload',
    loop_people: 'Person Verification',
    ownership_traversal: 'Ownership Chain',
    workflow_step: 'Workflow Action'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-800 flex items-center justify-center text-lg font-bold">{node.id}</div>
              <div>
                <div className="text-xs text-gray-500 uppercase">{node.stage?.replace(/^[A-Z]_/, '').replace(/_/g, ' ')}</div>
                <div className="text-sm font-medium text-gray-600">{typeLabels[node.type] || node.type}</div>
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-4">{node.question}</h3>

          {/* Answer */}
          {(answer || auditEntry?.answer) && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <div className="text-xs font-semibold text-blue-600 uppercase mb-1">Answer Provided</div>
              <div className="text-sm text-blue-900 font-medium">
                {typeof (answer || auditEntry?.answer) === 'object'
                  ? JSON.stringify(answer || auditEntry?.answer, null, 2)
                  : (answer || auditEntry?.answer)}
              </div>
            </div>
          )}

          {/* Score Impact */}
          {auditEntry && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className={`p-3 rounded-lg ${auditEntry.score_delta > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className={auditEntry.score_delta > 0 ? 'text-red-600' : 'text-green-600'}>Score Impact</span>
                </div>
                <div className={`text-2xl font-bold ${auditEntry.score_delta > 0 ? 'text-red-700' : 'text-green-700'}`}>
                  {auditEntry.score_delta > 0 ? '+' : ''}{auditEntry.score_delta || 0}
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase mb-1">
                  <Clock className="w-4 h-4" />
                  <span>Timestamp</span>
                </div>
                <div className="text-sm text-gray-900">{new Date(auditEntry.timestamp).toLocaleString()}</div>
              </div>
            </div>
          )}

          {/* Triggers Added */}
          {auditEntry?.triggers_added?.length > 0 && (
            <div className="p-3 bg-yellow-50 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-yellow-600 uppercase mb-2">
                <Zap className="w-4 h-4" />
                <span>Triggers Added</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {auditEntry.triggers_added.map((t, i) => (
                  <span key={i} className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded text-xs font-medium">
                    {t.replace('TR_', '').replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Next Node */}
          {node.next_node_id && (
            <div className="text-sm text-gray-500">
              <span className="font-medium">Next:</span> {node.next_node_id}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Stage Help Modal
const StageHelpModal = ({ stage, onClose }) => {
  if (!stage) return null;
  const info = STAGE_INFO[stage];
  if (!info) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-800 flex items-center justify-center">
                <HelpCircle className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{info.name}</h3>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
          </div>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-1">What happens in this stage?</h4>
              <p className="text-sm text-gray-600">{info.description}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-700 mb-1">Why is this important?</h4>
              <p className="text-sm text-blue-600">{info.purpose}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// People & Businesses Summary Component
const PeopleBusinessesSummary = ({ session }) => {
  const ubos = session.ubos || [];
  const directors = session.directors || [];
  const ownershipChain = session.ownership_chain || [];

  if (ubos.length === 0 && directors.length === 0 && ownershipChain.length === 0) {
    return null;
  }

  const getValidationStatus = (person) => {
    const checks = [];
    if (person.id_verified) checks.push({ label: 'ID Verified', status: 'pass' });
    if (person.address_verified) checks.push({ label: 'Address Verified', status: 'pass' });
    if (person.sanctions_clear) checks.push({ label: 'Sanctions Clear', status: 'pass' });
    if (person.pep_checked) checks.push({ label: 'PEP Checked', status: person.is_pep ? 'warning' : 'pass' });
    if (person.adverse_media_clear) checks.push({ label: 'Adverse Media Clear', status: 'pass' });
    return checks;
  };

  const getRiskIndicators = (person) => {
    const risks = [];
    if (person.is_pep) risks.push('Politically Exposed Person');
    if (person.high_risk_country) risks.push(`High-Risk Country: ${person.country}`);
    if (person.adverse_media) risks.push('Adverse Media Found');
    if (person.sanctions_match) risks.push('Potential Sanctions Match');
    return risks;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Users className="w-5 h-5 mr-2 text-indigo-600" /> People & Businesses Involved
      </h2>

      {/* UBOs */}
      {ubos.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <User className="w-4 h-4" /> Ultimate Beneficial Owners ({ubos.length})
          </h3>
          <div className="space-y-3">
            {ubos.map((ubo, i) => (
              <div key={i} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium text-gray-900">{ubo.name || 'Unknown'}</div>
                    <div className="text-sm text-gray-500">
                      {ubo.percentage && `${ubo.percentage}% ownership`}
                      {ubo.country && ` • ${ubo.country}`}
                      {ubo.role && ` • ${ubo.role}`}
                    </div>
                  </div>
                  {ubo.is_pep && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">PEP</span>
                  )}
                </div>
                {/* Validation checks */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {getValidationStatus(ubo).map((check, j) => (
                    <span key={j} className={`px-2 py-1 rounded text-xs font-medium ${
                      check.status === 'pass' ? 'bg-green-100 text-green-700' :
                      check.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                    }`}>
                      <CheckCircle2 className="w-3 h-3 inline mr-1" />{check.label}
                    </span>
                  ))}
                </div>
                {/* Risk indicators */}
                {getRiskIndicators(ubo).length > 0 && (
                  <div className="mt-2 p-2 bg-red-50 rounded">
                    <div className="text-xs font-semibold text-red-700 mb-1">Risk Indicators:</div>
                    {getRiskIndicators(ubo).map((risk, j) => (
                      <div key={j} className="text-xs text-red-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {risk}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Directors */}
      {directors.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Briefcase className="w-4 h-4" /> Directors & Officers ({directors.length})
          </h3>
          <div className="space-y-2">
            {directors.map((dir, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                <div>
                  <div className="font-medium text-gray-900">{dir.name || 'Unknown'}</div>
                  <div className="text-sm text-gray-500">{dir.role || 'Director'} {dir.appointed && `• Since ${dir.appointed}`}</div>
                </div>
                <div className="flex gap-1">
                  {dir.id_verified && <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">ID ✓</span>}
                  {dir.is_pep && <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">PEP</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ownership Chain */}
      {ownershipChain.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Ownership Chain ({ownershipChain.length} entities)
          </h3>
          <div className="space-y-2">
            {ownershipChain.map((entity, i) => (
              <div key={i} className="flex items-center gap-2">
                {i > 0 && <div className="w-4 h-4 border-l-2 border-b-2 border-gray-300 ml-2"></div>}
                <div className={`flex-1 p-3 rounded-lg ${entity.type === 'company' ? 'bg-blue-50' : 'bg-purple-50'}`}>
                  <div className="flex items-center gap-2">
                    {entity.type === 'company' ? <Building2 className="w-4 h-4 text-blue-600" /> : <User className="w-4 h-4 text-purple-600" />}
                    <span className="font-medium text-gray-900">{entity.name}</span>
                    {entity.percentage && <span className="text-sm text-gray-500">({entity.percentage}%)</span>}
                  </div>
                  {entity.jurisdiction && <div className="text-xs text-gray-500 ml-6">{entity.jurisdiction}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const KybSessionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [session, setSession] = useState(null);
  const [visualization, setVisualization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [showDecisionTree, setShowDecisionTree] = useState(true);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [controls, setControls] = useState('');
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedAuditEntry, setSelectedAuditEntry] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  // AI Risk Manager state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [aiSubmitting, setAiSubmitting] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);

  useEffect(() => { fetchSession(); }, [id]);

  useEffect(() => {
    if (session?.ai_analysis_status && session.ai_analysis_status !== 'NOT_STARTED') {
      fetchAiAnalysis();
    }
  }, [session?.id, session?.ai_analysis_status]);

  const fetchSession = async () => {
    try {
      const res = await api.get(`/kyb-levels/sessions/${id}/detail`);
      setSession(res.data.session);
      setVisualization(res.data.visualization);
    } catch (error) {
      console.error('Error fetching session:', error);
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const fetchAiAnalysis = async () => {
    try {
      const res = await api.get(`/kyb-levels/sessions/${id}/ai-analysis`);
      setAiAnalysis(res.data);
    } catch (error) {
      console.error('Error fetching AI analysis:', error);
    }
  };

  const startAiAnalysis = async () => {
    try {
      setAiLoading(true);
      const res = await api.post(`/kyb-levels/sessions/${id}/ai-analysis/start`);
      setAiAnalysis({ ...aiAnalysis, status: res.data.status, rounds_completed: res.data.round, current_questions: res.data.questions, conversation: [{ role: 'assistant', content: res.data, timestamp: new Date().toISOString() }] });
      setShowAiChat(true);
      toast.success('AI analysis started');
      fetchAiAnalysis();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to start AI analysis');
    } finally {
      setAiLoading(false);
    }
  };

  const submitAiResponse = async () => {
    if (!aiResponse.trim()) return;
    try {
      setAiSubmitting(true);
      await api.post(`/kyb-levels/sessions/${id}/ai-analysis/respond`, { response: aiResponse });
      setAiResponse('');
      fetchAiAnalysis();
      toast.success('Response submitted');
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to submit response');
    } finally {
      setAiSubmitting(false);
    }
  };

  const handleApproval = async (decision) => {
    if (!window.confirm(`Are you sure you want to ${decision.toLowerCase().replace('_', ' ')} this session?`)) return;
    try {
      setApproving(true);
      const body = { decision, notes: approvalNotes };
      if (decision === 'APPROVE_WITH_CONTROLS' && controls) {
        body.controls = controls.split('\n').filter(c => c.trim());
      }
      await api.post(`/kyb-levels/sessions/${id}/approve`, body);
      toast.success(`Session ${decision.toLowerCase().replace('_', ' ')} successfully`);
      fetchSession();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to process approval');
    } finally {
      setApproving(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      NOT_STARTED: { icon: Clock, color: 'bg-gray-100 text-gray-800', label: 'Not Started' },
      IN_PROGRESS: { icon: Clock, color: 'bg-blue-100 text-blue-800', label: 'In Progress' },
      PENDING_REVIEW: { icon: AlertTriangle, color: 'bg-yellow-100 text-yellow-800', label: 'Pending Review' },
      APPROVED: { icon: CheckCircle2, color: 'bg-green-100 text-green-800', label: 'Approved' },
      APPROVED_WITH_CONTROLS: { icon: Shield, color: 'bg-orange-100 text-orange-800', label: 'Approved with Controls' },
      REJECTED: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Rejected' },
      EXPIRED: { icon: Clock, color: 'bg-gray-100 text-gray-800', label: 'Expired' }
    };
    return badges[status] || badges.NOT_STARTED;
  };

  const getRiskColor = (tier) => ({ LOW: 'text-green-600', MEDIUM: 'text-yellow-600', HIGH: 'text-red-600', PROHIBITED: 'text-purple-600' }[tier] || 'text-gray-600');
  const canApprove = session?.status === 'PENDING_REVIEW' && (user?.user_type === 'PLATFORM_ADMIN' || user?.role === 'ADMIN');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  if (!session) return <div className="text-center py-12 text-gray-500">Session not found</div>;

  const status = getStatusBadge(session.status);
  const StatusIcon = status.icon;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/kyb-sessions')} className="flex items-center text-gray-600 hover:text-gray-900 mb-4"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Sessions</button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Building2 className="w-6 h-6 text-blue-600" />{session.company_name}</h1>
            <p className="text-gray-600 mt-1">Company Number: {session.company_number} {session.jurisdiction && `• ${session.jurisdiction}`}</p>
          </div>
          <div className="flex items-center gap-3">
            {session.status === 'IN_PROGRESS' && (
              <button
                onClick={() => navigate(`/kyb-sessions/${id}/continue`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                Continue Verification
              </button>
            )}
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${status.color}`}><StatusIcon className="w-4 h-4" />{status.label}</span>
          </div>
        </div>
        {session.flagged_for_review && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">This session has been flagged for manual review due to potential fraud detection</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Risk Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center"><Shield className="w-5 h-5 mr-2 text-blue-600" /> Risk Assessment</h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className={`text-3xl font-bold ${getRiskColor(session.risk_tier)}`}>{session.risk_score}</div>
                <div className="text-sm text-gray-500">Risk Score</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className={`text-xl font-bold ${getRiskColor(session.risk_tier)}`}>{session.risk_tier || 'N/A'}</div>
                <div className="text-sm text-gray-500">Risk Tier</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-xl font-bold text-gray-900">{session.decision || 'Pending'}</div>
                <div className="text-sm text-gray-500">Decision</div>
              </div>
            </div>
          </div>

          {/* Active Triggers */}
          {session.active_triggers?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center"><AlertTriangle className="w-5 h-5 mr-2 text-yellow-600" /> Active Triggers</h2>
              <div className="flex flex-wrap gap-2">
                {session.active_triggers.map((trigger, i) => (
                  <span key={i} className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">{trigger.replace('TR_', '').replace(/_/g, ' ')}</span>
                ))}
              </div>
            </div>
          )}

          {/* Evidence Collected */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center"><FileText className="w-5 h-5 mr-2 text-blue-600" /> Evidence Collected ({session.collected_evidence?.length || 0})</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {(session.collected_evidence || []).map((ev, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm text-green-800"><CheckCircle2 className="w-4 h-4" />{ev.replace('EV_', '').replace(/_/g, ' ')}</div>
              ))}
            </div>
          </div>

          {/* People & Businesses Summary */}
          <PeopleBusinessesSummary session={session} />

          {/* Decision Tree */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <button onClick={() => setShowDecisionTree(!showDecisionTree)} className="w-full flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center"><GitBranch className="w-5 h-5 mr-2 text-purple-600" /> Decision Tree Path ({session.completed_nodes?.length || 0} nodes completed)</h2>
              {showDecisionTree ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {showDecisionTree && visualization?.nodes && (
              <>
                <p className="text-sm text-gray-500 mb-4">Click on a completed node to see question details, answer, and scoring impact.</p>
                <DecisionTreeVisual
                  nodes={visualization.nodes}
                  completedNodes={session.completed_nodes || []}
                  auditLog={session.audit_log || []}
                  answers={session.answers || {}}
                  onNodeClick={(node, auditEntry) => {
                    setSelectedNode(node);
                    setSelectedAuditEntry(auditEntry);
                  }}
                  onStageHelp={(stage) => setSelectedStage(stage)}
                />
              </>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Approval Actions */}
          {canApprove && (
            <div className="bg-white rounded-xl shadow-sm border border-yellow-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center"><Activity className="w-5 h-5 mr-2 text-yellow-600" /> Manual Review Required</h3>
              <div className="space-y-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label><textarea value={approvalNotes} onChange={(e) => setApprovalNotes(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-lg p-2 text-sm" placeholder="Add review notes..." /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Controls (one per line)</label><textarea value={controls} onChange={(e) => setControls(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-lg p-2 text-sm" placeholder="Enhanced monitoring&#10;Quarterly reviews" /></div>
                <div className="space-y-2">
                  <button onClick={() => handleApproval('APPROVE')} disabled={approving} className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">Approve</button>
                  <button onClick={() => handleApproval('APPROVE_WITH_CONTROLS')} disabled={approving} className="w-full py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50">Approve with Controls</button>
                  <button onClick={() => handleApproval('REJECT')} disabled={approving} className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">Reject</button>
                </div>
              </div>
            </div>
          )}

          {/* Session Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Details</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-gray-500">Level</dt><dd className="font-medium">{session.level?.name || '-'}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Due Diligence</dt><dd className="font-medium">{session.level?.due_diligence_type || '-'}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Started</dt><dd>{session.started_at ? new Date(session.started_at).toLocaleString() : '-'}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Completed</dt><dd>{session.completed_at ? new Date(session.completed_at).toLocaleString() : '-'}</dd></div>
              {session.next_review_date && <div className="flex justify-between"><dt className="text-gray-500">Next Review</dt><dd>{new Date(session.next_review_date).toLocaleDateString()}</dd></div>}
            </dl>
          </div>

          {/* Applied Controls */}
          {session.applied_controls?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center"><Shield className="w-5 h-5 mr-2 text-orange-600" /> Applied Controls</h3>
              <ul className="space-y-2">{session.applied_controls.map((c, i) => <li key={i} className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-orange-600" />{c}</li>)}</ul>
            </div>
          )}

          {/* AI Risk Manager */}
          {session.status === 'PENDING_REVIEW' && user?.user_type === 'PLATFORM_ADMIN' && (
            <div className="bg-white rounded-xl shadow-sm border border-purple-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center"><Bot className="w-5 h-5 mr-2 text-purple-600" /> AI Risk Manager</h3>

              {(!aiAnalysis || aiAnalysis.status === 'NOT_STARTED') && (
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-4">Let AI analyze this application and ask clarifying questions about concerning areas.</p>
                  <button onClick={startAiAnalysis} disabled={aiLoading} className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center">
                    {aiLoading ? <><Loader className="w-4 h-4 mr-2 animate-spin" /> Starting...</> : <><Bot className="w-4 h-4 mr-2" /> Start AI Analysis</>}
                  </button>
                </div>
              )}

              {aiAnalysis && aiAnalysis.status !== 'NOT_STARTED' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Round {aiAnalysis.rounds_completed || 0} of {aiAnalysis.max_rounds || 5}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${aiAnalysis.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {aiAnalysis.status?.replace('_', ' ')}
                    </span>
                  </div>

                  {aiAnalysis.recommendation && (
                    <div className={`p-3 rounded-lg ${aiAnalysis.recommendation === 'APPROVE' ? 'bg-green-50 border border-green-200' : aiAnalysis.recommendation === 'REJECT' ? 'bg-red-50 border border-red-200' : 'bg-orange-50 border border-orange-200'}`}>
                      <div className="font-semibold text-sm">AI Recommendation: {aiAnalysis.recommendation.replace('_', ' ')}</div>
                      {aiAnalysis.recommended_workflows?.length > 0 && (
                        <div className="text-xs mt-1">Required workflows: {aiAnalysis.recommended_workflows.join(', ')}</div>
                      )}
                    </div>
                  )}

                  <button onClick={() => setShowAiChat(!showAiChat)} className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center text-sm">
                    <MessageSquare className="w-4 h-4 mr-2" /> {showAiChat ? 'Hide' : 'View'} Conversation
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* AI Chat Modal */}
      {showAiChat && aiAnalysis && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center"><Bot className="w-5 h-5 mr-2 text-purple-600" /> AI Risk Analysis Conversation</h3>
              <button onClick={() => setShowAiChat(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {(aiAnalysis.conversation || []).map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[80%] p-3 rounded-lg ${msg.role === 'assistant' ? 'bg-purple-50 text-gray-800' : 'bg-blue-500 text-white'}`}>
                    {msg.role === 'assistant' ? (
                      <div className="text-sm space-y-2">
                        {msg.content?.analysis && <p>{msg.content.analysis}</p>}
                        {msg.content?.risk_level && <p className="font-semibold">Risk Level: {msg.content.risk_level}</p>}
                        {msg.content?.questions?.length > 0 && (
                          <div className="mt-2">
                            <p className="font-semibold">Questions:</p>
                            <ul className="list-disc list-inside">{msg.content.questions.map((q, j) => <li key={j}>{q}</li>)}</ul>
                          </div>
                        )}
                        {msg.content?.recommendation && <p className="font-bold mt-2">Recommendation: {msg.content.recommendation}</p>}
                      </div>
                    ) : (
                      <p className="text-sm">{msg.content}</p>
                    )}
                    <p className="text-xs opacity-70 mt-1">{new Date(msg.timestamp).toLocaleTimeString()}</p>
                  </div>
                </div>
              ))}
            </div>
            {aiAnalysis.status === 'AWAITING_RESPONSE' && (
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <textarea value={aiResponse} onChange={(e) => setAiResponse(e.target.value)} placeholder="Type your response to the AI questions..." rows={2} className="flex-1 border border-gray-300 rounded-lg p-2 text-sm resize-none" />
                  <button onClick={submitAiResponse} disabled={aiSubmitting || !aiResponse.trim()} className="px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
                    {aiSubmitting ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Answer the AI's questions to help it make a more informed recommendation.</p>
              </div>
            )}
            {aiAnalysis.status === 'COMPLETED' && (
              <div className="p-4 border-t bg-gray-50 text-center text-sm text-gray-600">AI analysis complete. Review the recommendation above.</div>
            )}
          </div>
        </div>
      )}

      {/* Node Detail Modal */}
      {selectedNode && (
        <NodeDetailModal
          node={selectedNode}
          auditEntry={selectedAuditEntry}
          answer={session.answers?.[selectedNode.id]?.answer}
          onClose={() => { setSelectedNode(null); setSelectedAuditEntry(null); }}
        />
      )}

      {/* Stage Help Modal */}
      {selectedStage && (
        <StageHelpModal
          stage={selectedStage}
          onClose={() => setSelectedStage(null)}
        />
      )}
    </div>
  );
};

export default KybSessionDetail;

